import { useState } from 'react';
import { useLanguage } from '../../hooks/useLanguage';
import { useScrollSpy } from '../../hooks/useScrollSpy';
import { LanguageToggle } from '../ui/LanguageToggle';

/**
 * Navigation 组件的 Props
 */
interface NavigationProps {
  className?: string;
}

/**
 * 导航项接口
 */
interface NavItem {
  id: string;
  labelKey: string; // i18n key
  href: string; // 锚点链接
}

/**
 * 导航栏组件
 * 
 * 功能：
 * - 提供页面导航功能，支持平滑滚动
 * - 高亮显示当前激活的区域
 * - 固定在页面顶部（sticky 定位）
 * - 桌面端显示水平导航，移动端显示汉堡菜单
 * - 集成语言切换功能
 * 
 * @param props - 组件属性
 * @returns 导航栏组件
 */
export function Navigation({ className = '' }: NavigationProps) {
  const { t } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // 定义导航项
  const navItems: NavItem[] = [
    { id: 'hero', labelKey: 'nav.home', href: '#hero' },
    { id: 'features', labelKey: 'nav.features', href: '#features' },
    { id: 'use-cases', labelKey: 'nav.useCases', href: '#use-cases' },
    { id: 'screenshots', labelKey: 'nav.screenshots', href: '#screenshots' },
    { id: 'faq', labelKey: 'nav.faq', href: '#faq' },
  ];

  // 使用 useScrollSpy Hook 获取当前激活的区域
  const activeSection = useScrollSpy({
    sectionIds: navItems.map(item => item.id),
    offset: 100,
  });

  /**
   * 平滑滚动到目标区域
   * @param sectionId - 目标区域的 ID
   */
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
    // 移动端点击后关闭菜单
    setIsMenuOpen(false);
  };

  /**
   * 切换移动端菜单
   */
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <nav
      className={`
        sticky top-0 z-50
        bg-white/95 backdrop-blur-sm shadow-sm border-b border-gray-100
        ${className}
      `.trim()}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14">
          {/* Logo / 品牌名称 */}
          <div className="flex-shrink-0">
            <button
              onClick={() => scrollToSection('hero')}
              className="text-lg font-bold text-gray-900 hover:text-primary transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
              aria-label="返回首页"
            >
              Page Picker
            </button>
          </div>

          {/* 桌面端导航链接 */}
          <div className="hidden md:flex md:items-center md:space-x-6">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`
                  text-sm font-medium
                  transition-colors duration-200
                  focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded px-2 py-1
                  ${
                    activeSection === item.id
                      ? 'text-primary'
                      : 'text-gray-600 hover:text-gray-900'
                  }
                `.trim()}
                aria-label={`导航到${t(item.labelKey)}`}
                aria-current={activeSection === item.id ? 'page' : undefined}
              >
                {t(item.labelKey)}
              </button>
            ))}
            
            {/* 语言切换按钮 */}
            <LanguageToggle />
          </div>

          {/* 移动端汉堡菜单按钮 */}
          <div className="md:hidden flex items-center space-x-2">
            <LanguageToggle />
            <button
              onClick={toggleMenu}
              className="
                inline-flex items-center justify-center p-2
                text-gray-700 hover:text-primary hover:bg-gray-100
                rounded-md
                transition-colors duration-200
                focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
              "
              aria-label="打开菜单"
              aria-expanded={isMenuOpen}
            >
              {/* 汉堡菜单图标 */}
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                {isMenuOpen ? (
                  // 关闭图标 (X)
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  // 汉堡图标 (三条横线)
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* 移动端菜单面板 */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`
                  block w-full text-left px-3 py-2 rounded-md
                  text-base font-medium
                  transition-colors duration-200
                  focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
                  ${
                    activeSection === item.id
                      ? 'text-primary bg-primary-50'
                      : 'text-gray-700 hover:text-primary hover:bg-gray-50'
                  }
                `.trim()}
                aria-label={`导航到${t(item.labelKey)}`}
                aria-current={activeSection === item.id ? 'page' : undefined}
              >
                {t(item.labelKey)}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
